import type { AgentCursor as AgentCursorData } from "@/stores/canvas-store";
import { isVisibleAgentCursorWork } from "@/components/cursors/agent-work-visibility";
import { hasAgentCursorAttentionIntent } from "@/components/cursors/agent-cursor-intent";
import { cursorTargetIsOnDisplay } from "@/lib/agent-annotation";
import {
  conversationFrameForRun,
  cursorRunIdentity,
  type VisibleConversation,
} from "@/lib/run-surface-owner";

/** How far the agent cursor sits from the user's pointer, in screen pixels. */
export const COMPACT_CURSOR_POINTER_OFFSET = 16;

/**
 * The display whose window shows a cursor without a guidance target. A live
 * cursor's `displayId` records where the execution started; while the cursor
 * is still active it follows the hardware pointer, including across display
 * boundaries. Terminal or awaiting-input surfaces are pinned to their
 * captured display. A completed pointing cue does not change where ordinary
 * tool status follows the user.
 */
export function compactCursorOwnerDisplayId(
  cursor: AgentCursorData,
  activeDisplayId: number | null,
): number | null {
  const lockedDisplayId =
    cursor.frozen || cursor.error || cursor.question ? cursor.displayId : null;
  return lockedDisplayId ?? activeDisplayId;
}

/**
 * Pick the most recent compact cursor. When the avatar dock owns normal
 * status, callers pass `pointingOnly` so only screen-targeted cursors render.
 * A visible conversation frame that shows a cursor's run owns its status the
 * same way; `frameOwned` reports that for the picked cursor. Executions the
 * contextual onboarding guide presents are left to that guide.
 */
export function getCompactCursorState(
  cursors: Map<string, AgentCursorData>,
  {
    pointingOnly = false,
    projectId,
    displayId = null,
    activeDisplayId = null,
    visibleConversations = [],
    activeGuideId = null,
    contextualExecutionIds = [],
    conversationIdForCursor = () => null,
  }: {
    pointingOnly?: boolean;
    projectId?: string;
    displayId?: number | null;
    activeDisplayId?: number | null;
    visibleConversations?: readonly VisibleConversation[];
    activeGuideId?: string | null;
    contextualExecutionIds?: readonly string[];
    conversationIdForCursor?: (cursor: AgentCursorData) => string | null;
  } = {},
): {
  cursor: AgentCursorData | null;
  count: number;
  collapsed: boolean;
  frameOwned: boolean;
} {
  let latest: AgentCursorData | null = null;
  let latestFrameOwned = false;
  let count = 0;
  for (const c of cursors.values()) {
    if (c.executionId && contextualExecutionIds.includes(c.executionId)) {
      continue;
    }
    if (c.presentationDeliveryId && c.projectId && c.projectId !== projectId) {
      continue;
    }
    if (!isVisibleAgentCursorWork(c)) continue;
    const frameOwned =
      conversationFrameForRun(
        cursorRunIdentity(c, conversationIdForCursor(c)),
        visibleConversations,
      ) !== null;
    const hasAttentionIntent =
      hasAgentCursorAttentionIntent(c) ||
      (!!activeGuideId && c.presentationDeliveryId === activeGuideId);
    if ((pointingOnly || frameOwned) && !hasAttentionIntent) continue;
    const hasGuidanceTarget = !!c.pointTarget;
    const ownerDisplayId = compactCursorOwnerDisplayId(c, activeDisplayId);
    const cursorOnDisplay = hasGuidanceTarget
      ? cursorTargetIsOnDisplay(c, displayId)
      : displayId === null ||
        ownerDisplayId === null ||
        ownerDisplayId === displayId;
    if (!cursorOnDisplay) continue;
    count += 1;
    const hasPresentationDelivery = !!c.presentationDeliveryId;
    const latestHasPresentationDelivery = !!latest?.presentationDeliveryId;
    if (
      !latest ||
      (hasPresentationDelivery && !latestHasPresentationDelivery) ||
      (hasPresentationDelivery === latestHasPresentationDelivery &&
        c.lastSeen > latest.lastSeen)
    ) {
      latest = c;
      latestFrameOwned = frameOwned;
    }
  }
  const hasFinal =
    !!latest?.content ||
    !!latest?.contentFull ||
    !!latest?.error ||
    !!latest?.question ||
    !!latest?.frozen ||
    !!latest?.presentationDeliveryId;
  return {
    cursor: latest,
    count,
    collapsed: count > 1 && !hasFinal,
    frameOwned: latestFrameOwned,
  };
}
